import { ArrowLeft, CheckCircle2, Copy, Mail, PackageCheck, Send, XCircle } from "lucide-react";
import { useRef, useState, type FormEvent } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import { useActiveModules } from "../lib/active-modules";
import { openPreparedEmail, preparedEmailText } from "../lib/order-email";
import { isOfflineQueued } from "../lib/offline-link";
import { trpc } from "../lib/trpc";
import { MATERIAL_UNIT_PLURAL_LABELS } from "../../shared/material-units";

const unitLabels = MATERIAL_UNIT_PLURAL_LABELS;

const statusLabels = {
  draft: "Bozza",
  email_prepared: "Email pronta",
  sent: "Inviato",
  partially_received: "Arrivato in parte",
  received: "Ricevuto",
  cancelled: "Annullato",
} as const;

export function PurchaseOrderPage() {
  const { id = "" } = useParams();
  const utils = trpc.useUtils();
  const order = trpc.purchasing.orders.get.useQuery({ id }, { enabled: Boolean(id) });
  const prepareEmail = trpc.purchasing.orders.prepareEmail.useMutation();
  const confirmSent = trpc.purchasing.orders.confirmSent.useMutation();
  const cancelOrder = trpc.purchasing.orders.cancel.useMutation();
  // Il modulo Email automatiche: se e spento, la pagina resta esattamente com'era.
  const { attivi } = useActiveModules();
  const emailAutomatiche = attivi.has("email_automation");
  const sendByEmail = trpc.email.sendPurchaseOrder.useMutation();
  const impostazioniEmail = trpc.email.settings.useQuery(undefined, { enabled: emailAutomatiche });
  const invii = trpc.email.deliveries.useQuery({ entityId: id, limit: 10 }, { enabled: emailAutomatiche });
  const receive = trpc.purchasing.orders.receive.useMutation();
  const [notice, setNotice] = useState<string | null>(null);
  const [receiptLineId, setReceiptLineId] = useState<string | null>(null);
  const receiptOperationIds = useRef(new Map<string, string>());

  if (!id) return <Navigate to="/acquisti/ordini" replace />;

  async function refresh() {
    await Promise.all([
      utils.purchasing.orders.get.invalidate({ id }),
      utils.purchasing.orders.list.invalidate(),
      utils.purchasing.requests.list.invalidate(),
    ]);
  }

  async function inviaOra() {
    try {
      const esito = await sendByEmail.mutateAsync({ orderId: id });
      if (isOfflineQueued(esito)) {
        setNotice("Invio registrato sul dispositivo. Parte da solo appena torna la rete.");
        return;
      }
      await refresh();
      if (!esito) return;
      if (esito.status === "sent") setNotice(`Ordine inviato a ${esito.recipient}${esito.cc.length ? `, con copia a ${esito.cc.join(", ")}` : ""}.`);
      else if (esito.status === "preview") setNotice("Email pronta ma non spedita: il servizio di posta non e ancora configurato. L'ordine resta da inviare.");
      else setNotice(`Invio non riuscito: ${esito.error ?? "il servizio di posta ha rifiutato il messaggio."}`);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Invio non riuscito.");
    }
  }

  async function prepare() {
    try {
      await prepareEmail.mutateAsync({ orderId: id });
      await refresh();
      setNotice("Email preparata. Controllala prima di aprirla.");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Email non preparata.");
    }
  }

  async function openEmail() {
    const current = order.data;
    if (!current?.emailTo || !current.emailSubject || !current.emailBody) return;
    const result = await openPreparedEmail({ to: current.emailTo, subject: current.emailSubject, body: current.emailBody });
    setNotice(result.copied ? "Email troppo lunga: testo copiato negli appunti." : "Email aperta. Dopo l'invio torna qui e conferma.");
  }

  async function confirm() {
    await confirmSent.mutateAsync({ orderId: id });
    await refresh();
    setNotice("Invio confermato.");
  }

  async function cancel() {
    const reason = window.prompt("Motivo dell'annullamento");
    if (!reason) return;
    await cancelOrder.mutateAsync({ orderId: id, reason });
    await refresh();
    setNotice("Ordine annullato. Le richieste sono di nuovo disponibili.");
  }

  async function submitReceipt(event: FormEvent<HTMLFormElement>, lineId: string, unit: keyof typeof unitLabels) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const clientOperationId = receiptOperationIds.current.get(lineId) ?? crypto.randomUUID();
    receiptOperationIds.current.set(lineId, clientOperationId);
    try {
      await receive.mutateAsync({
        clientOperationId,
        orderLineId: lineId,
        quantity: Number(data.get("quantity")),
        unit,
        notes: String(data.get("notes") || "") || null,
        overrideExcess: data.get("overrideExcess") === "on",
      });
      await refresh();
      receiptOperationIds.current.delete(lineId);
      setReceiptLineId(null);
      setNotice("Arrivo materiale registrato.");
      form.reset();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Arrivo non registrato.");
    }
  }

  if (order.isLoading) return <p className="loading-copy"><PackageCheck size={18} /> Caricamento ordine...</p>;
  if (order.error) return <section className="purchase-page"><div className="query-error" role="alert"><strong>Ordine non disponibile</strong><span>{order.error.message}</span><button className="button button--outline" type="button" onClick={() => void order.refetch()}>Riprova</button></div></section>;
  if (!order.data) return <section className="purchase-page"><p className="form-error">Ordine non trovato.</p></section>;
  const current = order.data;
  const canReceive = ["sent", "partially_received", "received"].includes(current.status);
  const canCancel = ["draft", "email_prepared", "sent"].includes(current.status);

  return (
    <section className="purchase-page order-detail">
      <Link className="back-link" to="/acquisti/ordini"><ArrowLeft size={17} /> Torna agli ordini</Link>
      <header className="order-detail__hero">
        <div><p className="eyebrow">Ordine #{current.orderNumber}</p><h1>{current.supplierName}</h1><p>Creato il {new Intl.DateTimeFormat("it-IT", { dateStyle: "long" }).format(new Date(current.createdAt))}</p></div>
        <span className={`order-state order-state--${current.status}`}>{statusLabels[current.status]}</span>
      </header>

      {notice && <p className="notice" role="status">{notice}</p>}

      <section className="order-actions-panel">
        <div>
          <h2>Invio al fornitore</h2>
          <p>{emailAutomatiche
            ? impostazioniEmail.data?.delivers
              ? `L'app manda l'ordine da sola, da ${impostazioniEmail.data.fromEmail}, con copia al titolare.`
              : "Il servizio di posta non e ancora configurato: l'app prepara il messaggio e lo mostra, senza spedirlo."
            : "L'app prepara la mail; l'invio effettivo avviene nella tua applicazione email."}</p>
        </div>
        <div className="button-group">
          {emailAutomatiche && (current.status === "draft" || current.status === "email_prepared") && (
            <button className={`button button--primary${sendByEmail.isPending ? " button--saving" : ""}`} type="button" onClick={() => void inviaOra()}>
              <Send size={18} /> {sendByEmail.isPending ? "Invio..." : "Invia ora al fornitore"}
            </button>
          )}
          {current.status === "draft" && <button className="button button--outline" type="button" onClick={() => void prepare()} disabled={prepareEmail.isPending}><Mail size={18} /> Prepara email</button>}
          {current.status === "email_prepared" && <><button className="button button--outline" type="button" onClick={() => void openEmail()}><Send size={18} /> Apri email</button><button className="button button--outline" type="button" onClick={() => void confirm()} disabled={confirmSent.isPending}><CheckCircle2 size={18} /> Conferma invio</button></>}
          {canCancel && <button className="button button--ghost" type="button" onClick={() => void cancel()}><XCircle size={18} /> Annulla ordine</button>}
        </div>
      </section>

      {emailAutomatiche && (invii.data ?? []).length > 0 && (
        <section className="email-log">
          <h2>Invii di questo ordine</h2>
          <ul>
            {invii.data?.map((invio) => (
              <li key={invio.id} className={`email-log__riga email-log__riga--${invio.status}`}>
                <strong>{invio.status === "sent" ? "Inviata" : invio.status === "preview" ? "Solo preparata" : "Non riuscita"}</strong>
                <span>{new Intl.DateTimeFormat("it-IT", { dateStyle: "medium", timeStyle: "short" }).format(new Date(invio.createdAt))} · a {invio.recipient}</span>
                {invio.error && <small>{invio.error}</small>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {current.emailTo && current.emailSubject && current.emailBody && (
        <section className="email-preview"><header><div><span>A</span><strong>{current.emailTo}</strong></div><div><span>Oggetto</span><strong>{current.emailSubject}</strong></div><button className="button button--outline" type="button" onClick={() => void navigator.clipboard.writeText(preparedEmailText({ to: current.emailTo ?? "", subject: current.emailSubject ?? "", body: current.emailBody ?? "" }))}><Copy size={18} /> Copia email completa</button></header><pre>{current.emailBody}</pre></section>
      )}

      <section className="order-lines">
        <div className="section-title"><div><p className="eyebrow">Materiali</p><h2>Righe ordine</h2></div><span>{current.lines.length}</span></div>
        {current.lines.map((line) => <article className="order-line" key={line.id}>
            <div><h3>{line.labelSnapshot}</h3><p>{line.siteName}</p>{line.packageDescriptionSnapshot && <small>{line.packageDescriptionSnapshot}{line.packagesOrdered ? ` · ${line.packagesOrdered} confezioni` : ""}</small>}</div>
            <dl><div><dt>Ordinato</dt><dd>{Number(line.orderedQuantity)} {unitLabels[line.unit]}</dd></div><div><dt>Arrivato</dt><dd>{line.receivedQuantity} {unitLabels[line.unit]}</dd></div><div><dt>Manca</dt><dd>{line.missingQuantity} {unitLabels[line.unit]}</dd></div></dl>
            {line.requestSources.length ? <div className="order-line__history"><strong>Richieste di origine</strong>{line.requestSources.map((source) => <p key={source.id}>{source.label} · {source.siteName} · {new Intl.DateTimeFormat("it-IT", { dateStyle: "medium" }).format(new Date(source.requestedAt))}{source.requestedByName ? ` · ${source.requestedByName}` : ""}</p>)}</div> : null}
            {line.receipts.length ? <div className="order-line__history"><strong>Storico ricezioni</strong>{line.receipts.map((receipt) => <p key={receipt.id}>{Number(receipt.quantity)} {unitLabels[receipt.unit]} · {new Intl.DateTimeFormat("it-IT", { dateStyle: "medium", timeStyle: "short" }).format(new Date(receipt.receivedAt))}{receipt.receivedByName ? ` · ${receipt.receivedByName}` : ""}{receipt.notes ? ` · ${receipt.notes}` : ""}</p>)}</div> : null}
            {canReceive && line.missingQuantity > 0 && <button className="button button--outline" type="button" onClick={() => setReceiptLineId(receiptLineId === line.id ? null : line.id)}>Registra arrivo</button>}
            {receiptLineId === line.id && (
              <form className="receipt-form" onSubmit={(event) => void submitReceipt(event, line.id, line.unit)}>
                <label><span>Quantità arrivata, massimo previsto {line.missingQuantity}</span><input name="quantity" type="number" min="0.0001" step="any" required /></label>
                <label><span>Note</span><input name="notes" /></label>
                <label className="check-control"><input name="overrideExcess" type="checkbox" /> Consenti quantità superiore con motivazione nelle note</label>
                <button className="button button--primary" disabled={receive.isPending}>Salva arrivo</button>
              </form>
            )}
          </article>)}
      </section>

      {current.history.length > 0 && (
        <section className="order-lines" aria-labelledby="order-history-title">
          <div className="section-title"><div><p className="eyebrow">Audit</p><h2 id="order-history-title">Cronologia ordine</h2></div><span>{current.history.length}</span></div>
          {current.history.map((event) => (
            <article className="order-line" key={event.id}>
              <div>
                <h3>{event.previousStatus && event.nextStatus
                  ? `${statusLabels[event.previousStatus]} → ${statusLabels[event.nextStatus]}`
                  : event.nextStatus
                    ? statusLabels[event.nextStatus]
                    : "Aggiornamento ordine"}</h3>
                <p>{new Intl.DateTimeFormat("it-IT", { dateStyle: "medium", timeStyle: "short" }).format(new Date(event.occurredAt))}{event.userName ? ` · ${event.userName}` : ""}</p>
              </div>
            </article>
          ))}
        </section>
      )}
    </section>
  );
}
